import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import { REFETCH_FADE_TRANSITION_CLASS, REFETCHING_OPACITY_CLASS } from '@/styles/refetchFade';
import { appVersionCheckQueryOptions } from '../api/appVersionCheck.api';
import { AppVersionCheckFilterPanel } from '../components/AppVersionCheckFilterPanel';
import { AppVersionCheckSummary } from '../components/AppVersionCheckSummary';
import {
  AppVersionDepartmentSection,
  AppVersionDepartmentSkeleton,
} from '../components/AppVersionDepartmentSection';
import { useAppVersionCheckFilters } from '../hooks/useAppVersionCheckFilters';
import { filterAppVersionReport } from '../utils/filterAppVersionReport';

const DEPARTMENT_SKELETON_COUNT = 3;
const DEPARTMENT_SKELETON_KEYS = Array.from(
  { length: DEPARTMENT_SKELETON_COUNT },
  (_, department) => `skeleton-department-${department}`,
);

export function AppVersionCheckPage() {
  const controller = useAppVersionCheckFilters();
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    appVersionCheckQueryOptions(controller.reportParams),
  );

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const appliedSearchText = useDebounce(searchText).trim();
  const isSearching = appliedSearchText !== '';

  const visibleReport = useMemo(
    () => (data ? filterAppVersionReport(data, appliedSearchText) : undefined),
    [data, appliedSearchText],
  );

  const emptyState = isSearching
    ? {
        kind: 'noResults' as const,
        title: `No members match "${appliedSearchText}"`,
        description: 'Try another name, HR ID or mobile.',
      }
    : {
        kind: 'noData' as const,
        title: 'No members found',
        description: 'No members have logged in with an app version for these filters.',
      };

  return (
    <div className="flex flex-col gap-page-gap">
      <PageHeader
        title="App Version Check"
        search={{ value: searchText, onChange: setSearchText, placeholder: 'Name, HR ID, Mobile' }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AppVersionCheckFilterPanel controller={controller} />
      </FilterPanelCollapse>

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the app version report"
          onRetry={() => void refetch()}
        />
      ) : isLoading ? (
        <>
          <AppVersionCheckSummary totals={undefined} />
          <div className="flex flex-col gap-3">
            {DEPARTMENT_SKELETON_KEYS.map((skeletonKey, index) => (
              <AppVersionDepartmentSkeleton key={skeletonKey} isOpen={index === 0} />
            ))}
          </div>
        </>
      ) : visibleReport && visibleReport.departments.length > 0 ? (
        <>
          <AppVersionCheckSummary totals={visibleReport} />

          <div
            key={String(isSearching)}
            className={cn(
              'flex flex-col gap-3',
              REFETCH_FADE_TRANSITION_CLASS,
              isFetching && REFETCHING_OPACITY_CLASS,
            )}
          >
            {visibleReport.departments.map((department, index) => (
              <AppVersionDepartmentSection
                key={department.departmentId ?? department.departmentName}
                department={department}
                defaultOpen={isSearching || index === 0}
                searchText={isSearching ? appliedSearchText : undefined}
              />
            ))}
          </div>
        </>
      ) : (
        <EmptyState {...emptyState} />
      )}
    </div>
  );
}
